import {
  Flex,
  Box,
  Avatar,
  Icon,
  Grid,
  Center,
  Button,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
} from '@chakra-ui/react';
import { SanitizedBox } from '@/ui/SanitizedDom';
import { Time } from '@/ui/Time';
import { conversationConstants } from './constants';
import { AttachmentPreview } from '@/components/outreach-attachment/AttachmentPreview';
import { Mail02 } from '@kula-ai/icons/outline/Mail02';
import { ChevronDown } from '@kula-ai/icons/solid/ChevronDown';
import { useState } from 'react';
import { ChevronUp } from '@kula-ai/icons/outline/ChevronUp';
import { CornerUpLeft } from '@kula-ai/icons/solid/CornerUpLeft';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';
import { Send01 } from '@kula-ai/icons/outline/Send01';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { DotsVertical } from '@kula-ai/icons/solid/DotsVertical';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { FiEdit } from 'react-icons/fi';
import moment from 'moment';
import { useDateFormat } from '@/hooks';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { ATTACHMENT_API_SERVICE } from './api';
import { useShowToast } from '@/hooks';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle } from '@kula-ai/icons/outline/CheckCircle';
import { getErrorMessage } from '@/utils/errors';
// Imports
import { useApplicationHelper as useApplicationHelperDefault } from '@/components/contacts/candidate-job-drawer/useApplicationHelper'; //('todo:cv2');
import { useApplicationHelper as useApplicationHelperV1 } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useContext } from 'react';
import { atsAssessmentRecordType } from './utils';
import { useSendScheduledAssessmentNowMutation } from '@/store/query/assessment';
import { KButton } from '@/ui-library/components';
import { convertPlainTextToHtml } from '@/utils/textarea-parser';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { Atom01 } from '@kula-ai/icons/dual-tone/Atom01';
import { AISessionTranscript } from '@/components/ai-coordinator-sessions/components/AISessionTranscript';
import { AISessionAudioPlayer } from '@/components/ai-coordinator-sessions/components/AISessionAudioPlayer';
// import { CoordinatorCallStatusBadge } from '@/components/ats/job-stage-actions/actions/interview/ScheduleWithAi/CoordinatorCallStatusBadge';

const hasTimeElapsed = (scheduled_at, timezone) => {
  return moment.tz(scheduled_at, timezone).isBefore(moment.tz(timezone));
};

const getTransferInfoForBadge = (transfer_info, atsApplicationId) => {
  if (!transfer_info) return null;
  const appTransferInfo =
    transfer_info?.applications?.[String(atsApplicationId)];
  const hasTransferredIn = !!appTransferInfo?.transferred_in;
  const hasTransferredOut = !!appTransferInfo?.transferred_out;
  const fromApplicationId =
    appTransferInfo?.transferred_in?.from_application_id;
  // TransferredBadge uses history: for transferred_in we pass [from_application_id]; for transferred_out we pass [current app id]
  const history = hasTransferredIn
    ? fromApplicationId
      ? [fromApplicationId]
      : []
    : hasTransferredOut
      ? [atsApplicationId]
      : [];
  return hasTransferredIn || hasTransferredOut
    ? {
        applications: transfer_info.applications || {},
        history,
      }
    : null;
};

const getMessageStatusText = (conversation) => {
  if (
    conversation?.status === 'scheduled' &&
    conversation?.channel === 'email'
  ) {
    return 'scheduled to send to';
  } else {
    const channelText =
      conversationConstants[conversation?.channel]?.value || '';
    const platformText =
      conversation?.channel === 'kula_platform'
        ? ` through the ${
            conversation?.schedule?.substituted_name ||
            conversation?.schedule?.name
          } slot booking screen`
        : '';
    return `sent ${channelText}${platformText} to`;
  }
};

const MessageContent = ({
  conversation,
  isFirstMessage = false,
  message,
  onReply,
  hasThread,
  onReplyCandidate,
  onSendNow,
  onDeleteEmail,
  onEditEmail,
  isFromApplication = false,
  isUpdating: _isUpdating = false,
  onRefetchScheduled,
  transferInfo = null,
}) => {
  const { feHtmlToText } = useKFlags();
  const { resourceUsage } = useContext(CandidateContext);
  const applicationHelper = useApplicationHelperDefault();
  const applicationHelperV2 = useApplicationHelperV1();
  const { applicationData } =
    resourceUsage === 'V2' ? applicationHelperV2 : applicationHelper;
  const { getIsAcceptedFileType } = useFileValidation();
  const { feEmailReply } = useFlags();
  const { feScheduleRevamp, feSharedEmails } = useKFlags();
  const { timeFormat } = useDateFormat();
  const { scheduled_at, is_transferred = false } = conversation || {};
  const toast = useShowToast();
  const [attachmentStates, setAttachmentStates] = useState({});
  const applicationId = applicationData?.id;
  const ScheduledDate = moment.parseZone(scheduled_at).format('MMM D YYYY');
  const ScheduledTime = moment.parseZone(scheduled_at).format(timeFormat);

  const {
    mutateAsync: addEmailAttachmentsToFiles,
    isPending: isAddingAttachments,
  } = useMutation({
    mutationFn: ATTACHMENT_API_SERVICE.addEmailAttachmentsToFiles,
  });

  const [sendScheduledAssessmentNow, { isLoading: isSendingAssessment }] =
    useSendScheduledAssessmentNowMutation();

  const handleFileAttachment = async (attachment) => {
    try {
      // here conversation is the main message for the content and messages are the other messages in the same convo thread
      const response = await addEmailAttachmentsToFiles({
        application_id: conversation?.ats_application_id || applicationId,
        candidate_message_id: conversation?.id,
        attachment_id: attachment?.id,
      });
      if (response?.errors?.length) {
        toast({
          title: getErrorMessage(response?.errors, 'attachments'),
          status: 'error',
        });
      } else {
        setAttachmentStates((prev) => ({
          ...prev,
          [attachment?.id]: 'success',
        }));
      }
    } catch (error) {
      console.error('Error adding attachment:', error);
    }
  };

  const scheduledAssessmentNow = async () => {
    const result = await sendScheduledAssessmentNow({
      assessmentId: conversation?.record?.id,
    });
    if (result?.error?.length) {
      toast({ title: 'Sending assessment failed', status: 'error' });
    } else {
      toast({ title: 'Assessment sent successfully' });
      if (onRefetchScheduled) {
        onRefetchScheduled();
      }
    }
  };

  const renderAttachmentAction = (attachment) => {
    // Get current state
    const attachmentStatus =
      attachmentStates[attachment?.id] ||
      (attachment?.is_added_to_files === true ? 'success' : 'idle');
    const isSuccess =
      attachmentStatus === 'success' || attachment?.is_added_to_files === true;

    // Check if file type is supported and under size limit (30MB)
    const isValidFileType = getIsAcceptedFileType(attachment);
    const isValidFileSize = attachment.byte_size < 30 * 1024 * 1024;
    const isFileValid = isValidFileType && isValidFileSize;

    if (isSuccess) {
      return (
        <Tooltip
          label="Added to files section"
          shouldWrapChildren
          hasArrow
          placement="top"
        >
          <Icon
            as={CheckCircle}
            w="18px"
            h="18px"
            color="success-200"
            marginTop={'6px'}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label={
          !isValidFileType
            ? 'File type is not supported'
            : !isValidFileSize
              ? 'File size is too large'
              : ''
        }
        shouldWrapChildren
        hasArrow
        placement="top"
      >
        <Flex
          gap="6px"
          alignItems="center"
          color="primary-500"
          cursor={!isFileValid ? 'not-allowed' : 'pointer'}
          onClick={() =>
            isFileValid &&
            !isAddingAttachments &&
            handleFileAttachment(attachment)
          }
          opacity={!isFileValid ? 0.5 : 1}
        >
          <Icon as={PlusCircle} w="18px" h="18px" color="primary-500" />
          <KText as="span" color="primary-500">
            Add to files
          </KText>
        </Flex>
      </Tooltip>
    );
  };

  const renderMoreMenu = () => {
    // Handle sending and deleting scheduled assessment for AtsAssessment record type
    if (conversation?.record?.type === atsAssessmentRecordType) {
      if (conversation?.status === 'scheduled') {
        return (
          <KButton
            variant="ats-secondary"
            size="sm"
            onClick={scheduledAssessmentNow}
            isLoading={isSendingAssessment}
            data-test-id="conversation-assessment-send-now-button"
          >
            <Flex gap="5px" alignItems="center">
              <Icon
                as={FiEdit}
                height="14px"
                width="14px"
                color="ats-primary"
              />
              <KText size="sm-semi-bold" color="ats-primary">
                Send now
              </KText>
            </Flex>
          </KButton>
        );
      }
      return null;
    }
    return (
      <Menu>
        <MenuButton>
          <Box
            bg="white"
            p="3px 6px 0px 6px"
            borderRadius="6px"
            borderWidth="1px"
            borderColor="border-color"
            data-test-id="conversation-email-more-menu-button"
          >
            <Icon
              as={DotsVertical}
              color="secondary-700"
              w="16px"
              h="16px"
              verticalAlign="top"
              mt="2px"
            />
          </Box>
        </MenuButton>
        <MenuList zIndex={9} borderRadius="8px">
          <MenuItem gap="8px" onClick={() => onSendNow(message)}>
            <Icon
              as={Send01}
              width="14px"
              height="14px"
              color="secondary-700"
            />
            <KText
              size="sm-medium"
              color="secondary-700"
              data-test-id="conversation-email-send-now-button"
            >
              Send now
            </KText>
          </MenuItem>
          <MenuItem gap="8px" onClick={() => onEditEmail(message)}>
            <Icon
              as={Edit05}
              width="14px"
              height="14px"
              color="secondary-700"
            />
            <KText
              size="sm-medium"
              color="secondary-700"
              data-test-id="conversation-email-edit-button"
            >
              Edit email
            </KText>
          </MenuItem>
          <MenuItem
            gap="8px"
            onClick={() => onDeleteEmail && onDeleteEmail(message)}
          >
            <Icon as={Trash01} width="14px" height="14px" color="#E53535" />
            <KText
              size="sm-medium"
              color="#E53535"
              data-test-id="conversation-email-delete-button"
            >
              Delete email
            </KText>
          </MenuItem>
        </MenuList>
      </Menu>
    );
  };

  return (
    <Box
      bg="secondary-25"
      borderRadius="14px"
      p="20px"
      ml="12px"
      flex="1"
      mb={hasThread ? '12px' : '24px'}
    >
      <Flex justifyContent="space-between" gap="12px">
        <Flex direction="column" gap="8px">
          {isFirstMessage &&
            ((conversation?.ats_job && conversation?.ats_job?.id) ||
              (conversation?.outreach && conversation?.outreach?.id)) && (
              <Flex
                p="4px 8px"
                borderRadius="sm"
                bg="white"
                border="1px solid"
                borderColor="border-color"
                alignItems="center"
                gap="6px"
                key={conversation?.ats_job?.id || conversation?.outreach?.id}
                width="fit-content"
              >
                <KText size="sm-medium">
                  {conversation?.ats_job ? 'Job' : 'Flow'}:
                </KText>
                <Link
                  href={
                    conversation?.ats_job?.id
                      ? `/jobs/${conversation?.ats_job?.id}?tab=candidates&appId=${conversation?.ats_application_id}`
                      : `/flows/${conversation?.outreach?.id}/candidates`
                  }
                  isExternal
                  display="flex"
                  gap="4px"
                  alignItems="center"
                  _hover={{ textDecoration: 'none' }}
                >
                  <TruncatedTextWithTooltip
                    text={
                      conversation?.ats_job?.name ||
                      conversation?.outreach?.title
                    }
                    color="primary-600"
                    maxW="400px"
                    size="sm-medium"
                  />
                  <Icon as={HiArrowTopRightOnSquare} color="primary-600" />
                </Link>
              </Flex>
            )}
          <Flex alignItems="center" gap="6px">
            {isFirstMessage && (
              <KText
                size="md-semi-bold"
                color="secondary-900"
                wordBreak="break-word"
              >
                {conversation?.subject}
              </KText>
            )}
            {is_transferred && <TransferredBadge transferInfo={transferInfo} />}
          </Flex>
        </Flex>
        {feEmailReply && isFirstMessage && conversation?.repliable && (
          <Button
            variant="ats-secondary"
            cursor="pointer"
            minWidth="fit-content"
            onClick={() => onReply(message)}
          >
            <Flex gap="5px">
              <Icon
                as={CornerUpLeft}
                height="16px"
                width="16px"
                color="#4A4A4A"
              />
              <KText
                size="sm-semi-bold"
                data-test-id="conversation-email-reply-button"
              >
                Reply
              </KText>
            </Flex>
          </Button>
        )}
        {!conversation?.repliable &&
          conversation?.scheduled_at &&
          (conversation?.sent_at === null &&
          hasTimeElapsed(conversation?.scheduled_at, conversation?.timezone) ? (
            <Flex
              alignItems="center"
              border="1px solid"
              borderColor="border-color"
              p="2px 8px"
              borderRadius="xs"
            >
              <KText size="sm-medium" color="secondary-700">
                Sending email
              </KText>
            </Flex>
          ) : (
            <Flex alignItems="center" gap="12px" w="max-content">
              <Flex alignItems="center" gap="4px" w="max-content">
                <KText size="sm-medium">Scheduled:</KText>
                <KText>
                  {ScheduledDate}, {ScheduledTime}
                </KText>
              </Flex>
              {renderMoreMenu()}
            </Flex>
          ))}
      </Flex>
      <Flex justifyContent="space-between" mt="12px">
        <Flex justifyContent="start">
          <Avatar
            className="avatar-sm"
            name={conversation?.sender?.name}
            src={conversation?.sender?.avatar}
            borderRadius="4px"
            width="20px"
            height="20px"
          />
          <KText ml="4px">
            <KText as="span" color="secondary-900" size="sm-medium" mr="4px">
              {conversation?.sender?.name}
            </KText>
            <KText as="span" mr="4px">
              {getMessageStatusText(conversation)}
            </KText>
            <KText as="span" color="secondary-900" size="sm-medium" mr="4px">
              {conversation?.cc?.length > 0 && conversation?.bcc?.length > 0
                ? `${conversation?.recipient?.name},`
                : conversation?.cc?.length > 0
                  ? `${conversation?.recipient.name} and`
                  : conversation?.recipient?.name}
            </KText>
            {conversation?.sent_from?.name && (
              <>
                <KText as="span" size="sm-medium" mr="4px">
                  from
                </KText>
                <KText
                  as="span"
                  size="sm-medium"
                  mr="4px"
                  color="secondary-900"
                >
                  {feSharedEmails
                    ? conversation?.sent_from?.name
                    : 'Kula no-reply'}
                </KText>
                <KText as="span" size="sm-medium" mr="4px">
                  {feSharedEmails
                    ? `<${conversation?.sent_from?.email}>`
                    : '<no-reply@kula.ai>'}
                </KText>
              </>
            )}
            {conversation?.cc && conversation.cc.length > 0 && (
              <KText as="span" size="sm-medium" mr="4px">
                CC: {conversation?.cc?.map((user) => user.name).join(', ')}
              </KText>
            )}

            {conversation?.bcc && conversation.bcc.length > 0 && (
              <KText as="span" size="sm-medium" mr="4px">
                and BCC:{' '}
                {conversation?.bcc?.map((user) => user.name).join(', ')}
              </KText>
            )}
            {conversation?.ats_job?.id && (
              <>
                <KText as="span" mr="4px">
                  in job
                </KText>
                <KText as="span" color="secondary-900" size="sm-medium">
                  {conversation?.ats_job?.name}
                </KText>
              </>
            )}
          </KText>
          <KText as="span" ml="4px">
            <Time
              time={conversation?.sent_at}
              variant="muted"
              whiteSpace="nowrap"
              size="sm-regular"
            />
          </KText>
        </Flex>
        {feScheduleRevamp &&
          isFirstMessage &&
          conversation?.channel === 'kula_platform' && (
            <Button
              variant="ats-secondary"
              cursor="pointer"
              minWidth="fit-content"
              onClick={() => onReplyCandidate(message)}
            >
              <Flex gap="5px">
                <Icon
                  as={CornerUpLeft}
                  height="16px"
                  width="16px"
                  color="#4A4A4A"
                />
                <KText
                  size="sm-semi-bold"
                  data-test-id="conversation-email-reply-to-candidate-button"
                >
                  Reply
                </KText>
              </Flex>
            </Button>
          )}
      </Flex>
      <Box mt="12px" className="editor-style">
        {conversation?.channel !== 'self_schedule' && (
          <SanitizedBox
            fontSize="14px"
            html={
              feHtmlToText
                ? convertPlainTextToHtml(conversation?.body || '')
                : conversation?.body
            }
            whiteSpace="pre-line"
            color="secondary-700"
          />
        )}
      </Box>
      {conversation?.attachments?.length > 0 && (
        <Grid gap="10px" p="20px" templateColumns="repeat(2, 1fr)">
          {conversation?.attachments?.map((attachment, idx) => {
            // Prepare attachment for display
            const displayFile = {
              ...attachment,
              name: attachment?.filename ?? attachment?.name,
              type: attachment?.content_type ?? attachment?.type,
              size: attachment?.byte_size ?? attachment?.size,
            };

            const attachmentAction = renderAttachmentAction(attachment);

            return (
              <AttachmentPreview
                key={idx}
                file={displayFile}
                bg="#fff"
                AfterContent={
                  (conversation?.ats_job?.id ||
                    (isFromApplication && applicationId)) &&
                  conversation?.category !== 'candidate_adhoc' &&
                  attachmentAction
                }
              />
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

const Conversation = ({
  conversation = {},
  otherMessages = [],
  handleReplyClick = () => {},
  handleCandidateReply = () => {},
  handleSendNow = () => {},
  handleDeleteEmail = () => {},
  handleEditEmail = () => {},
  isFromApplication = false,
  isUpdating = false,
  onRefetchScheduled,
  transferInfo = null,
}) => {
  const {
    icon = Mail02,
    bg = 'pastel-3-light',
    color = 'pastel-3-dark',
  } = conversationConstants[conversation?.channel];
  const { feEmailReply } = useFlags();
  const [showMore, setShowMore] = useState(false);

  const lastMessage = otherMessages?.length
    ? otherMessages[otherMessages?.length - 1]
    : conversation;

  return (
    <Flex className="card" data-test-id="conversation-email-section">
      <Flex flexDir="column" alignItems="center">
        <Center w="34px" h="34px" borderRadius="full" bg={bg}>
          <Icon as={icon} width="17px" height="17px" color={color} />
        </Center>
        <Box flex="1" borderColor="light.border" className="border" />
      </Flex>
      <Flex flexDir="column" flex="1">
        <MessageContent
          conversation={conversation}
          isFirstMessage
          onReply={handleReplyClick}
          onReplyCandidate={handleCandidateReply}
          onSendNow={handleSendNow}
          onDeleteEmail={handleDeleteEmail}
          onEditEmail={handleEditEmail}
          message={lastMessage}
          hasThread={otherMessages?.length > 0}
          isFromApplication={isFromApplication}
          isUpdating={isUpdating}
          onRefetchScheduled={onRefetchScheduled}
          transferInfo={transferInfo}
        />
        {otherMessages?.length > 0 && (
          <>
            {showMore &&
              otherMessages?.map((item, index) => (
                <MessageContent
                  key={index}
                  message={item}
                  conversation={item}
                  onReply={handleReplyClick}
                  onSendNow={handleSendNow}
                  onDeleteEmail={handleDeleteEmail}
                  onEditEmail={handleEditEmail}
                  hasThread={otherMessages?.length > 0}
                  isFromApplication={isFromApplication}
                  isUpdating={isUpdating}
                  onRefetchScheduled={onRefetchScheduled}
                  transferInfo={transferInfo}
                />
              ))}
            <Center
              bg="ats-bg-primary"
              ml="12px"
              cursor="pointer"
              h="60px"
              mb="24px"
              borderRadius="sm"
              onClick={() => setShowMore((prev) => !prev)}
              position="relative"
              gap="12px"
            >
              <KText size="sm-semi-bold" color="ats-primary">
                {showMore
                  ? 'Hide thread'
                  : `Show ${otherMessages?.length} more email${
                      otherMessages?.length > 1 ? 's' : ''
                    }`}
              </KText>
              <Icon
                as={showMore ? ChevronUp : ChevronDown}
                color="ats-primary"
              />
              {feEmailReply && showMore && lastMessage?.repliable && (
                <Button
                  position="absolute"
                  right="20px"
                  variant="ats-secondary"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplyClick(lastMessage);
                  }}
                >
                  <Flex gap="5px">
                    <Icon
                      as={CornerUpLeft}
                      height="16px"
                      width="16px"
                      color="#4A4A4A"
                    />
                    <KText
                      size="sm-semi-bold"
                      data-test-id="conversation-email-show-more-reply-button"
                    >
                      Reply
                    </KText>
                  </Flex>
                </Button>
              )}
            </Center>
          </>
        )}
      </Flex>
    </Flex>
  );
};

const AISessionConversation = ({ session, candidateName }) => {
  const hasTranscript = session?.transcript && session.transcript.length > 0;
  const hasRecording =
    !!session?.recording_url && session?.recording_duration_seconds !== null;

  // Map conversation session to activityData shape expected by CoordinatorCallStatusBadge
  // const activityData =
  //   session !== null
  //     ? { ...session, ai_call_status: session?.status ?? 'unknown' }
  //     : null;

  return (
    <Flex className="card" data-test-id="conversation-ai-session-section">
      <Flex flexDir="column" alignItems="center">
        <Center w="34px" h="34px" borderRadius="full" bg="secondary-950">
          <Icon as={Atom01} width="17px" height="17px" color="white" />
        </Center>
        <Box flex="1" borderColor="light.border" className="border" />
      </Flex>
      <Flex flexDir="column" flex="1">
        <Box
          bg="primary-25"
          borderRadius="14px"
          p="20px"
          ml="12px"
          flex="1"
          mb="24px"
        >
          <Flex
            alignItems="baseline"
            mb="12px"
            gap="12px"
            justifyContent="space-between"
          >
            {/* <SessionStatusBadge status={session?.status || 'unknown'} /> */}
            <KText
              size="md-semi-bold"
              fontWeight="600"
              color="secondary-900"
              w="80%"
            >
              {session?.direction === 'outbound'
                ? 'Kula Coordinator has called the candidate to schedule a time for screening interview'
                : 'Candidate has scheduled a time for screening interview'}
            </KText>
            <Flex
              shrink={0}
              gap="8px"
              alignItems="center"
              justifyContent="flex-end"
              w="20%"
            >
              {/* <CoordinatorCallStatusBadge
                activityData={activityData}
                isActiveSession={false}
              /> */}
              <Time
                time={session?.created_at}
                size="xs-regular"
                color="secondary-500"
              />
            </Flex>
          </Flex>

          {hasTranscript && (
            <Box mb={hasRecording ? '16px' : 0}>
              <AISessionTranscript
                transcript={session.transcript}
                candidateName={candidateName || session.candidateName}
                candidateAvatarUrl={session.candidateAvatarUrl}
              />
            </Box>
          )}

          {hasRecording && (
            <AISessionAudioPlayer
              recordingUrl={session.recording_url}
              durationSeconds={session.recording_duration_seconds || 0}
            />
          )}

          {!hasTranscript && !hasRecording && (
            <Flex justifyContent="center" py="8px">
              <KText size="sm-regular" color="secondary-500">
                No transcript or recording available for this session.
              </KText>
            </Flex>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export const ConversationContent = ({
  conversations = [],
  handleReplyClick = () => {},
  handleCandidateReply = () => {},
  handleSendNow = () => {},
  handleDeleteEmail = () => {},
  handleEditEmail = () => {},
  isFromApplication = false,
  isUpdating = false,
  onRefetchScheduled,
  transferInfo = null,
  transfer_info = null,
  candidateName = null,
}) => {
  const isUnifiedFormat =
    conversations?.length > 0 &&
    conversations[0] &&
    typeof conversations[0] === 'object' &&
    'type' in conversations[0] &&
    'data' in conversations[0];

  return (
    <Box mt="16px">
      {conversations?.map((item, idx) => {
        if (isUnifiedFormat) {
          const { type, data } = item;
          const key =
            type === 'ai_session' ? `session-${data?.id}` : `email-${idx}`;
          if (type === 'ai_session') {
            return (
              <AISessionConversation
                key={key}
                session={data}
                candidateName={candidateName}
              />
            );
          }
          const firstMessage = data[data?.length - 1];
          const otherMessages = data?.slice(0, -1)?.reverse() || [];
          return (
            <Conversation
              key={key}
              conversation={firstMessage}
              otherMessages={otherMessages}
              handleReplyClick={handleReplyClick}
              handleCandidateReply={handleCandidateReply}
              handleSendNow={handleSendNow}
              handleDeleteEmail={handleDeleteEmail}
              handleEditEmail={handleEditEmail}
              isFromApplication={isFromApplication}
              isUpdating={isUpdating}
              onRefetchScheduled={onRefetchScheduled}
              transferInfo={transferInfo}
            />
          );
        }
        const conversation = item;
        const firstMessage = conversation[conversation?.length - 1];
        const otherMessages = conversation?.slice(0, -1)?.reverse();
        const threadTransferInfo = transfer_info
          ? getTransferInfoForBadge(
              transfer_info,
              firstMessage?.ats_application_id
            )
          : transferInfo;
        return (
          <Conversation
            key={idx}
            conversation={firstMessage}
            otherMessages={otherMessages}
            handleReplyClick={handleReplyClick}
            handleCandidateReply={handleCandidateReply}
            handleSendNow={handleSendNow}
            handleDeleteEmail={handleDeleteEmail}
            handleEditEmail={handleEditEmail}
            isFromApplication={isFromApplication}
            isUpdating={isUpdating}
            onRefetchScheduled={onRefetchScheduled}
            transferInfo={threadTransferInfo}
          />
        );
      })}
    </Box>
  );
};
